import { Api, ApiUrls } from '@/Api';
import { putRequest } from '@/ApiHelpers';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { ViewerLayoutDefault, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { defineStore, storeToRefs } from 'pinia';

export interface UserStore {
  id?: string;
}
export interface UserCredentials {
  email?: string;
  password?: string;
}

export interface UserState {
  id?: string;
  name?: string;
  email?: string;
  isFree?: boolean;
  avatarUrl?: string;
  itemCount?: number;
  confirmetAt?: string;
  createdAt?: string;
  dropLimit?: number;
  softDropLimitExceeded?: boolean;
  organizationId?: string;
  /** Hashids of every org the viewer is a member of (owner, admin, member,
   *  view-only). The server's user serializer emits all memberships via
   *  `has_many :organizations`; we preserve the full list so that
   *  same-org checks can account for users who are members of an org
   *  without it being their primary. */
  organizationIds: string[];
  alternativeSoftDropLimitExceeded?: boolean;
  role?: string; // User permission level (user, vip, admin)
  profile?: string; // Job role (engineering, sales, etc.)
  jwt?: string;
  isImpersonating?: boolean; // System admin impersonating another user
  pusher: {
    channel?: string;
    channelV5?: string;
  };
  isUserOrgAdmin?: boolean; // Organization admin status from user serializer
  isUserOrgViewOnly?: boolean; // Organization view-only status from user serializer
  isSystemAdmin?: boolean; // System admin status (role === 'admin')
  smartActionSetting?: string;
  smartActionSettingType?: string;
  viewerLayout?: ViewerMode | ViewerLayoutDefault;
  preferences?: any;
}

export const useUserStore = defineStore('userStore', {
  state: (): UserState => ({
    id: undefined,
    name: undefined,
    email: undefined,
    isFree: undefined,
    avatarUrl: undefined,
    itemCount: undefined,
    confirmetAt: undefined,
    createdAt: undefined,
    dropLimit: undefined,
    softDropLimitExceeded: undefined,
    organizationId: undefined,
    organizationIds: [],
    alternativeSoftDropLimitExceeded: undefined,
    role: undefined,
    profile: undefined,
    jwt: undefined,
    isImpersonating: undefined,
    pusher: {
      channel: undefined,
      channelV5: undefined,
    },
    isUserOrgAdmin: undefined,
    isUserOrgViewOnly: undefined,
    isSystemAdmin: undefined,
    smartActionSetting: undefined,
    smartActionSettingType: undefined,
    viewerLayout: undefined,
    preferences: {},
  }),
  actions: {
    initialize(userData: any) {
      const orgRelations = userData?.data?.relationships?.organizations?.data ?? [];
      this.$patch({
        id: userData.data.id,
        avatarUrl: userData.data.links?.avatarUrl,
        organizationId: orgRelations[0]?.id,
        organizationIds: orgRelations.map((o: { id: string }) => o.id),
        organization: userData?.currentOrganization,
        ...userData.data.attributes,
        preferences: userData.data.attributes.preferences || {},
      });

      return userData;
    },
    load() {
      return Api.user.currentUser().then(response => this.initialize({ data: response?.data?.user }));
    },
    signIn(credentials: UserCredentials) {
      if (!credentials.email || !credentials.password) {
        return Promise.reject('Missing user or password data');
      }

      const { id } = storeToRefs(useViewerItemStore());
      return Api.user.signIn(credentials.email, credentials.password, id?.value);
    },
    signUp(credentials: UserCredentials, signedUpFrom: string | null = null, utmContent: string | null = null) {
      if (!credentials.email || !credentials.password) {
        return Promise.reject('Missing user or password data');
      }

      const { id } = storeToRefs(useViewerItemStore());
      return Api.user.signUp(credentials.email, credentials.password, id?.value, signedUpFrom, utmContent);
    },
    async updateProfile(profileData: { name?: string; profile?: string }) {
      if (!this.id) {
        throw new Error('User ID is required for profile updates');
      }

      // Use API endpoint with user ID for profile updates
      const response = await putRequest(ApiUrls.user.update.replace('{{id}}', this.id), profileData);

      // Update the store state with the returned user data
      // If response is successful (status 200), update local state
      this.$patch({
        name: profileData.name || this.name,
        profile: profileData.profile || this.profile,
      });

      return response;
    },
  },
  getters: {
    isLoggedIn: (state: UserState) => !!state.id,
    itemCapacityPercent: (state: UserState) => {
      const itemCount = state?.itemCount;
      const limitDrops = state.dropLimit;

      if (!itemCount || !limitDrops) {
        return 0;
      }

      return Math.min(Math.round((itemCount / limitDrops) * 100), 100);
    },
    isOrgUserAdmin: (state: UserState) => state.isUserOrgAdmin,
    isOrgUserViewOnly: (state: UserState) => state.isUserOrgViewOnly,
  },
});
